import React, { useEffect, useState } from "react";
import Dashboard from "./dashboard";
import axios from "../api/baseurl";
import "./Spinner.css";
import "./Loader.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Pagination } from "react-bootstrap";
import { FaRedoAlt } from "react-icons/fa";
import "./hm-theme.css";

function EmailStatistics() {
  const [EmailStatistics, setEmailStatistics] = useState([]);
  const [loader, setLoader] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const token = JSON.parse(localStorage.getItem("data")).token;

  const navigate = useNavigate();
  const formatDateToYMD = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;

  // Set default dates on mount
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const today = new Date();
    setFromDate(formatDateToYMD(firstDay));
    setToDate(formatDateToYMD(today));
  }, []);

  // Reset to page 0 when date filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [fromDate, toDate]);

  // Fetch data only when dates are set and page changes
  useEffect(() => {
    if (fromDate && toDate) {
      getData(currentPage);
    }
  }, [currentPage, fromDate, toDate]);

  const getData = async (pageNumber = 0) => {
    try {
      setLoader(true);
      const payload = { fromDate, toDate };
      const response = await axios.post(
        "/api/email-statistics/getAllEmailStatistics",
        payload,
        {
          params: { page: pageNumber },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEmailStatistics(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    getData(newPage);
  };

  const handleRetry = async (id) => {
    try {
      setLoader(true);
      await axios.post(
        `/api/email-statistics/resendFailedEmails/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email resent successfully");
      getData(currentPage);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    }

    if (currentPage >= totalPages - 3) {
      startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  return (
    <div>
      <Dashboard />
      {loader && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">
          {/* Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">mark_email_unread</i>
                Email Statistics
              </h2>
              <p className="hm-page-subtitle">
                Track outgoing system email delivery logs and retry failed email transmissions.
              </p>
            </div>
          </div>

          {/* Search & Date Filter Card */}
          <div className="hm-card mb-4">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">filter_alt</i>
                Search & Date Filter
              </h3>
            </div>
            <div className="hm-card-body p-3">
              <div className="row g-3 align-items-end">
                <div className="col-md-6 col-lg-3">
                  <label htmlFor="fromDate" className="form-label small fw-semibold">
                    From Date
                  </label>
                  <input
                    type="date"
                    id="fromDate"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-md-6 col-lg-3">
                  <label htmlFor="toDate" className="form-label small fw-semibold">
                    To Date
                  </label>
                  <input
                    type="date"
                    id="toDate"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="hm-card">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">receipt_long</i>
                Email Delivery Statistics
              </h3>
            </div>
            <div className="hm-card-body p-0">
              <div style={{ overflowX: "auto" }}>
                <table className="hm-table">
                  <thead>
                    <tr>
                      <th>Sl.No.</th>
                      <th>Date Time</th>
                      <th>To Recipient</th>
                      <th>CC Recipients</th>
                      <th>Message ID</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {EmailStatistics.length === 0 ? (
                      <tr>
                        <td colSpan="6">
                          <div className="hm-empty-state py-4">
                            <i className="material-icons">mail</i>
                            <p>{loader ? "Loading statistics..." : "No email statistics available"}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      EmailStatistics.map((stats, index) => (
                        <tr key={stats.id}>
                          <td>{currentPage * 10 + index + 1}</td>
                          <td>{stats.dateTime}</td>
                          <td className="fw-semibold">{stats.toRecipient}</td>
                          <td>{stats.ccRecipient || "-"}</td>
                          <td className="small text-muted font-monospace">{stats.msgId}</td>
                          <td className="text-center">
                            <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                              <span
                                className={`hm-badge ${
                                  stats.status === "SENT"
                                    ? "hm-badge-success"
                                    : stats.status === "FAILED"
                                    ? "hm-badge-danger"
                                    : "hm-badge-secondary"
                                }`}
                              >
                                {stats.status}
                              </span>
                              {stats.status === "FAILED" &&
                                stats.toRecipient !== null &&
                                stats.toRecipient !== "" && (
                                  <button
                                    className="btn btn-warning btn-sm rounded-pill px-2 py-0 d-inline-flex align-items-center gap-1 mt-1"
                                    style={{ fontSize: "0.75rem" }}
                                    title="Retry sending email"
                                    onClick={() => handleRetry(stats.id)}
                                  >
                                    <FaRedoAlt style={{ fontSize: "0.7rem" }} />
                                    Retry
                                  </button>
                                )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="hm-pagination p-3 border-top d-flex justify-content-center">
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => handlePageChange(0)}
                      disabled={currentPage === 0}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                    />
                    {renderPageNumbers()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages - 1}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages - 1)}
                      disabled={currentPage === totalPages - 1}
                    />
                  </Pagination>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailStatistics;

